import { Box, Grid, Link, Typography } from "@material-ui/core"
import CategoryAreaListItem from "components/Common/CategoryAreaListItem"
import HoverText from "components/Common/HoverText"
import SeatMemberListItem from "components/Common/SeatMemberListItem"
import AppContext from "contexts/App"
import { useFormikContext } from "formik"
import PropTypes from "prop-types"
import { useContext } from "react"
import capitalize from "utils/capitalize"
import transformCaseAndSpaces from "utils/transformCaseAndSpaces"

import useStyles from "./styles"

const Listing = ({
  areasOfInterest,
  areasOfExpertise,
  areasOfExperience,
  entityInfo = {},
}) => {
  const { values } = useFormikContext()
  const { authUser } = useContext(AppContext)
  const classes = useStyles()

  let showSuggestedRequirements =
    values.minimumDegree !== "" ||
    values.occupationRequired !== "" ||
    values.interest.length > 0

  const displayOccupation = () => {
    if (values?.occupation_required === "true") {
      return "Yes"
    } else if (values?.occupation_required === "false") {
      return "No"
    } else {
      return transformCaseAndSpaces(values?.occupation_required)
    }
  }

  const findOpenPositions = values?.positions.filter(
    (x) => x.member.currently_vacant
  )
  const findfilledPositions = values?.positions.filter(
    (x) => !x.member.currently_vacant
  )
  const openPositions = findOpenPositions?.length
  const filledPositions = findfilledPositions?.length
  const totalPositions = filledPositions + openPositions

  const showReviewPeriods = () => {
    if (authUser?.review_period_rolling === 1) {
      return (
        <Typography className={classes.infoBoxData} variant="body1">
          Rolling
        </Typography>
      )
    } else {
      return authUser?.review_periods?.map((x, i) => {
        let start = capitalize(x.split(",")[0].trim())
        let end = capitalize(x.split(",")[1].trim())
        return (
          <Typography key={i} className={classes.infoBoxData} variant="body1">
            {start}
            {" - "}
            {end}
          </Typography>
        )
      })
    }
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.firstSubContainer}>
        <Box marginTop={1} marginBottom={1} display="flex" flexDirection="row">
          <Typography
            color="primary"
            variant="body1"
            className={classes.entityName}
          >
            {authUser?.entity
              ? `${authUser?.entity}, ${authUser?.state}`
              : `${entityInfo?.entity}, ${entityInfo?.state}`}
          </Typography>
          <Typography variant="body1" className={classes.entityType}>
            {authUser?.entity_type
              ? capitalize(authUser?.entity_type)
              : capitalize(entityInfo?.entity_type)}
          </Typography>
        </Box>
        <Typography variant="h6">{values?.name}</Typography>
      </Box>
      {/* start first data row */}
      <Box className={classes.dataContainer}>
        <Grid container spacing={2} className={classes.dataRow}>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>POSITIONS</Typography>
            <Typography className={classes.infoBoxData} variant="body1">
              <span style={{ fontWeight: "bold" }}>{openPositions}</span> of{" "}
              <span style={{ fontWeight: "bold" }}>{totalPositions}</span>{" "}
              available
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>TERM</Typography>
            <Typography className={classes.infoBoxData} variant="body1">
              {transformCaseAndSpaces(values?.term)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              APPOINTING AUTHORITY
            </Typography>
            <Typography className={classes.infoBoxData} variant="body1">
              {transformCaseAndSpaces(values?.appointing_authority)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              ENABLING LEGISLATION
            </Typography>
            <Typography
              className={classes.urlLink}
              variant="body1"
              onClick={() =>
                window.open(
                  values?.enabling_legislation?.includes("http")
                    ? values?.enabling_legislation
                    : "https://" + values?.enabling_legislation
                )
              }
            >
              {values?.enabling_legislation ? (
                <Link target="_blank" color="inherit" underline="always">
                  <HoverText
                    variant="body1"
                    desktopSize={60}
                    phoneSize={20}
                    data={values?.enabling_legislation}
                  />
                </Link>
              ) : (
                ""
              )}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* end first row */}
      {/* second row */}
      <Box className={classes.dataContainer}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              TYPE OF APP
            </Typography>
            <Typography className={classes.infoBoxData} variant="body1">
              {capitalize(values?.app_type)}
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              WEBSITE & URL
            </Typography>
            <Typography
              className={classes.urlLink}
              variant="body1"
              onClick={() =>
                window.open(
                  values?.board_url?.includes("http")
                    ? values?.board_url
                    : "https://" + values?.board_url
                )
              }
            >
              {values?.board_url ? (
                <Link target="_blank" color="inherit" underline="always">
                  <HoverText
                    variant="body1"
                    desktopSize={60}
                    phoneSize={20}
                    data={values?.board_url}
                  />
                </Link>
              ) : (
                ""
              )}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              RE-APPOINTMENT
            </Typography>
            <Typography className={classes.infoBoxData} variant="body1">
              {transformCaseAndSpaces(values?.reappointment)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              REVIEW PERIODS
            </Typography>
            <Box>{showReviewPeriods()}</Box>
          </Grid>
        </Grid>
      </Box>
      {/* end second row*/}
      {/* start third row */}
      <Box className={classes.dataContainer}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography className={classes.infoBoxTitle}>
              MINUTES URL
            </Typography>
            <Typography
              className={classes.urlLink}
              variant="body1"
              onClick={() =>
                window.open(
                  values?.minutes_url?.includes("http")
                    ? values?.board_url
                    : "https://" + values?.board_url
                )
              }
            >
              {values?.minutes_url ? (
                <Link target="_blank" color="inherit" underline="always">
                  <HoverText
                    variant="body1"
                    desktopSize={60}
                    phoneSize={20}
                    data={values?.minutes_url}
                  />
                </Link>
              ) : (
                <Box height="14px" />
              )}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* end third row*/}

      <Box className={classes.summaryContainer}>
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {values.summary}
        </Typography>
      </Box>
      {showSuggestedRequirements ? (
        <Box className={classes.suggestedContainer}>
          <Typography className={classes.suggestedTitle}>
            SUGGESTED REQUIREMENTS
          </Typography>
          <Box>
            {values.minimum_degree !== "" ? (
              <Box>
                <Typography className={classes.label}>Education</Typography>
                <Typography>
                  {transformCaseAndSpaces(values.minimum_degree)}
                </Typography>
              </Box>
            ) : null}
            {values?.occupation_required ? (
              <Box>
                <Typography className={classes.label}>
                  Occupation Required
                </Typography>
                <Typography>{displayOccupation()}</Typography>
              </Box>
            ) : null}
          </Box>
          <Box display="flex">
            <Box width="33%">
              <Typography className={classes.label}>
                Areas of Interest
              </Typography>
              {areasOfInterest.map((x, i) => (
                <CategoryAreaListItem key={i} value={x} />
              ))}
              {/* {values?.all_interest && (
                  <CategoryAreaListItem value={"All Interest Types Welcome"} />
                )} */}
            </Box>
            <Box width="33%">
              <Typography className={classes.label}>
                Areas of Expertise
              </Typography>
              {areasOfExpertise.map((x, i) => (
                <CategoryAreaListItem key={i} value={x} />
              ))}
              {values?.all_expertise && (
                <CategoryAreaListItem value={"All Expertise Types Welcome"} />
              )}
            </Box>
            <Box width="33%">
              <Typography className={classes.label}>
                Areas of Experience
              </Typography>
              {areasOfExperience.map((x, i) => (
                <CategoryAreaListItem key={i} value={x} />
              ))}
              {values?.all_experience && (
                <CategoryAreaListItem value={"All Experience Types Welcome"} />
              )}
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box className={classes.seatMembersContainer}>
        <Typography className={classes.membersTitle}>
          CURRENT SEAT MEMBERS
        </Typography>
        <Grid container spacing={3}>
          {values?.positions?.map((p, i) => {
            const passedMember = {
              ...p?.member,
              id: p?.id,
              position: p?.position,
            }
            return (
              <Grid item key={i} xs={4}>
                <SeatMemberListItem
                  member={passedMember}
                  vacant={passedMember?.first_name ? false : true}
                  boardTerm={values?.term}
                />
              </Grid>
            )
          })}
        </Grid>
      </Box>
    </Box>
  )
}

Listing.propTypes = {
  tabValue: PropTypes.number,
  areasOfInterest: PropTypes.array,
  areasOfExpertise: PropTypes.array,
  areasOfExperience: PropTypes.array,
  entityInfo: PropTypes.object,
}

export default Listing
